import { AnyAction, createSlice, Slice } from '@reduxjs/toolkit';
import { IEndUser } from 'modules/EndUsersManagement/interfaces/EndUser';
import { RootStateOrAny } from 'react-redux';
import { INIT_USERS_STATS } from '../constants/graphs';
import {
	IEndUsersCountStatsByDate,
	IEndUsersCountStatsByRegion,
	IEndUsersCountStatsByRegionLesson,
} from '../interfaces/Stats';

export const DASHBOARD_KEY_IN_STORE = 'dashboard';

interface DashboardState {
	isLoading: boolean;
	globalCounts: {
		totalUsers: number;
		totalLessons: number;
		totalVocabWords: number;
	};
	activeNewUsersCounts: {
		isLoading: boolean;
		data: IEndUsersCountStatsByDate;
	};

	regionsUsersCount: {
		isLoading: boolean;
		data: IEndUsersCountStatsByRegion[];
		regionLessonsData: IEndUsersCountStatsByRegionLesson[];
	};

	recentUsers: {
		isLoading: boolean;
		data: IEndUser[];
	};
}

const initState: DashboardState = {
	isLoading: false,
	globalCounts: {
		totalUsers: 0,
		totalLessons: 0,
		totalVocabWords: 0,
	},
	activeNewUsersCounts: {
		isLoading: false,
		data: INIT_USERS_STATS,
	},

	regionsUsersCount: {
		isLoading: false,
		data: [],
		regionLessonsData: [],
	},

	recentUsers: {
		isLoading: false,
		data: [],
	},
};

const dashboardSlice: Slice = createSlice({
	name: DASHBOARD_KEY_IN_STORE,
	initialState: initState,
	reducers: {
		getDashboardData: (state: DashboardState) => {
			state.isLoading = true;
			state.activeNewUsersCounts.isLoading = true;
			state.regionsUsersCount.isLoading = true;
			state.recentUsers.isLoading = true;
		},
		handleGetDashboardDataSuccess: (
			state: DashboardState,
			action: AnyAction,
		) => {
			const {
				newUsersCount,
				regionsUsersCounts,
				globalCounts,
				mostRecentUsers,
			} = action.payload;
			state.isLoading = false;
			state.globalCounts = globalCounts;
			state.activeNewUsersCounts = {
				isLoading: false,
				data: newUsersCount,
			};
			state.regionsUsersCount = {
				isLoading: false,
				data: regionsUsersCounts,
				regionLessonsData: [],
			};
			state.recentUsers = {
				isLoading: false,
				data: mostRecentUsers,
			};
		},
		getActiveUsersCounts(state: DashboardState, action: AnyAction) {
			state.activeNewUsersCounts.isLoading = true;
		},
		getNewUsersCounts(state: DashboardState, action: AnyAction) {
			state.activeNewUsersCounts.isLoading = true;
		},
		handleGetActiveNewUsersCountsSuccess: (
			state: DashboardState,
			action: AnyAction,
		) => {
			state.activeNewUsersCounts = {
				isLoading: false,
				data: action.payload,
			};
		},
		getRegionsUsersCounts(state: DashboardState) {
			state.regionsUsersCount.isLoading = true;
		},
		getRegionLessonsUsersCounts(state: DashboardState) {
			state.regionsUsersCount.isLoading = true;
		},
		handleGetRegionsUsersCountsSuccess(
			state: DashboardState,
			action: AnyAction,
		) {
			state.regionsUsersCount.isLoading = false;
			state.regionsUsersCount.data = action.payload;
		},
		handleGetRegionLessonsUsersCountsSuccess(
			state: DashboardState,
			action: AnyAction,
		) {
			state.regionsUsersCount.isLoading = false;
			state.regionsUsersCount.regionLessonsData = action.payload;
		},
	},
});

export const {
	getDashboardData,
	handleGetDashboardDataSuccess,
	getActiveUsersCounts,
	getNewUsersCounts,
	handleGetActiveNewUsersCountsSuccess,
	getRegionsUsersCounts,
	getRegionLessonsUsersCounts,
	handleGetRegionsUsersCountsSuccess,
	handleGetRegionLessonsUsersCountsSuccess,
} = dashboardSlice.actions;

export const DashboardStateSelector = (state: RootStateOrAny): DashboardState =>
	state[DASHBOARD_KEY_IN_STORE];

export const dashboardSliceReducer = dashboardSlice.reducer;

export default dashboardSlice;
